/**
 * @fileoverview Connect a source revision/checksum to proofreading runs and review comments. Backend run identity and idempotency protect duplicate submission; the panel owns polling and explicit comment-resolution actions. `kc-internal-backend/functions/document_proofreading.py` owns generation and persisted findings. Reviewing or resolving a finding is separate from changing source document bytes.
 */

import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import AppIcon from '../shared/AppIcon';
import './DocumentProofreadPanel.css';

const ACTIVE = new Set(['queued', 'extracting', 'reviewing', 'validating', 'building_comments']);
const READY = new Set(['completed', 'partial']);


const errorText = error => error?.response?.data?.error || error?.message || 'Proofreading could not be loaded.';


export const proofreadRequestKey = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;



function CommentAuthor({ comment }) {
  const name = comment.author_type === 'ai' ? 'AI Proofreader' : String(comment.author_name || comment.author_id || 'Reviewer');
  const date = comment.created_at && new Date(typeof comment.created_at === 'number' ? comment.created_at * 1000 : comment.created_at);
  const hasDate = date && !Number.isNaN(date.getTime());
  return <div className="document-comment-author">
    <span className={`document-comment-avatar ${comment.author_type === 'ai' ? 'is-ai' : ''}`} aria-hidden="true">
      {comment.author_type === 'ai' ? <AppIcon icon="pi pi-sparkles" /> : name.split(/\s+/).map(word => word[0]).slice(0, 2).join('').toUpperCase()}
    </span>
    <div><strong>{name}</strong>{hasDate && <time dateTime={date.toISOString()} title={date.toLocaleString()}>{date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · {date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</time>}</div>
    {comment.author_type === 'ai' && <span className="document-proofread-ai">AI-generated</span>}
  </div>;
}



function threadReplies(comments, rootId) {
  const ids = new Set([rootId]);
  let added = true;
  while (added) {
    added = false;
    for (const comment of comments) {
      if (!ids.has(comment.comment_id) && ids.has(comment.parent_comment_id)) {
        ids.add(comment.comment_id); added = true;
      }
    }
  }
  return comments.filter(comment => comment.comment_id !== rootId && ids.has(comment.comment_id));
}



export default function DocumentProofreadPanel({ documentId, sourceVersion, requestKey, expanded,
  onExpand, onRunChange, onLocate, onWordOpened, pane = false, onCommentsChange }) {
  const [run, setRun] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsError, setCommentsError] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [reply, setReply] = useState('');
  const [filter, setFilter] = useState('active');
  const [selectedId, setSelectedId] = useState(null);
  const [composing, setComposing] = useState(false);
  const contentRef = useRef(null);
  const commentsChange = useRef(onCommentsChange);
  commentsChange.current = onCommentsChange;
  const generation = useRef(0);
  const startedRequests = useRef(new Set());
  const runChange = useRef(onRunChange);
  runChange.current = onRunChange;
  const base = `/simul/archive/documents/${encodeURIComponent(documentId)}/proofreads`;
  const revisionId = sourceVersion?.revision_id;
  const sourceHash = sourceVersion?.content_sha256;
  const active = ACTIVE.has(run?.status);
  const ready = READY.has(run?.status);
  const commentsBase = `/simul/archive/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(revisionId || '')}/comments`;
  const roots = comments.filter(comment => !comment.parent_comment_id);
  const openCount = roots.filter(comment => !comment.resolved_at).length;
  const visibleRoots = roots.filter(comment => filter === 'all' || Boolean(comment.resolved_at) === (filter === 'resolved'));
  const reviewId = run?.source_revision_id === revisionId ? run?.run_id : null;

  useEffect(() => { runChange.current?.(run); }, [run]);
  useEffect(() => { commentsChange.current?.(comments); }, [comments]);
  useEffect(

  () => {
    setComments([]); setSelectedId(null); setComposing(false); setFilter('active');
    setRun(null); setHistory([]); setError(''); setReplyTo(null); setReply('');
  }, [commentsBase]);

  useEffect(

  () => {
    let stopped = false;
    setCommentsError('');
    if (!revisionId) return undefined;
    setCommentsLoading(true);
    api.get(commentsBase, { params: reviewId ? { run_id: reviewId } : {} })
      .then(response => { if (!stopped) setComments(response.data.comments || []); })
      .catch(err => { if (!stopped) setCommentsError(errorText(err)); })
      .finally(() => { if (!stopped) setCommentsLoading(false); });
    return () => { stopped = true; };
  }, [commentsBase, revisionId, reviewId, run?.status]);

  useEffect(

  () => {
    const sequence = ++generation.current;
    if (!expanded || !revisionId) { setBusy(false); return undefined; }
    setError('');
    let stopped = false;
    setBusy(true);
    const read = 

    async () => {
      const response = await api.get(base, { params: { revision_id: revisionId } });
      const runs = response.data?.runs || [];
      if (stopped || sequence !== generation.current) return;
      setHistory(runs);
      if (requestKey && !startedRequests.current.has(requestKey)) {
        const created = await api.post(base, { source_revision_id: revisionId,
          source_content_sha256: sourceHash, idempotency_key: requestKey });
        startedRequests.current.add(requestKey);
        if (!stopped && sequence === generation.current) setRun(created.data.run);
      } else if (runs[0]) {
        const detail = await api.get(`${base}/${encodeURIComponent(runs[0].run_id)}`);
        if (!stopped && sequence === generation.current) setRun(detail.data.run);
      }
    };
    read().catch(err => { if (!stopped) setError(errorText(err)); })
      .finally(() => { if (!stopped) setBusy(false); });
    return () => { stopped = true; };
  }, [base, revisionId, sourceHash, requestKey, expanded]);

  useEffect(

  () => {
    if (!active || !run?.run_id) return undefined;
    let stopped = false;
    let timer;
    const poll = 

    async () => {
      try {
        const response = await api.get(`${base}/${encodeURIComponent(run.run_id)}`);
        if (!stopped) { setRun(response.data.run); setError(''); }
      } catch (err) { if (!stopped) setError(errorText(err)); }
      if (!stopped) timer = setTimeout(poll, 2000);
    };
    timer = setTimeout(poll, 1200);
    return () => { stopped = true; clearTimeout(timer); };
  }, [active, base, run?.run_id]);

  const act = 

  async action => {
    if (busy) return;
    const sequence = generation.current;
    setBusy(true); setError('');
    try { await action(() => sequence === generation.current); }
    catch (err) { if (sequence === generation.current) setError(errorText(err)); }
    finally { if (sequence === generation.current) setBusy(false); }
  };
  const refresh = 

  async current => {
    const response = await api.get(commentsBase, { params: run?.run_id ? { run_id: run.run_id } : {} });
    if (current()) setComments(response.data.comments || []);
  };
  const start = 

  () => act(async current => {
    const response = await api.post(base, { source_revision_id: revisionId, source_content_sha256: sourceHash,
      idempotency_key: proofreadRequestKey(), rerun: Boolean(run) });
    if (current()) setRun(response.data.run);
  });
  const dispose = 

  (comment, disposition) => act(async current => {
    await api.post(`/simul/archive/comments/${encodeURIComponent(comment.comment_id)}/resolve`, {
      resolved: disposition !== 'open', disposition,
    });
    if (current() && selectedId === comment.comment_id) { setSelectedId(null); onLocate?.(null); }
    await refresh(current);
  });
  const download = 

  () => act(

  async () => {
    const response = await api.get(`${commentsBase}/docx`, { responseType: 'blob', params: run?.run_id ? { run_id: run.run_id } : {} });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url; link.download = `Comments-${sourceVersion?.label || 'review'}.docx`;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
  const word = 

  () => act(

  async current => {
    const response = await api.post(`${commentsBase}/open-in-word`, { run_id: run?.run_id || null });
    if (!current()) return;
    if (!response.data?.edit_session?.office_uri) throw new Error('The Word editing link was not returned.');
    if (onWordOpened) onWordOpened(response.data);
    else window.location.href = response.data.edit_session.office_uri;
  });

  const select = 

  (comment, anchor = comment.anchors?.find(item => item.quote)) => {
    setSelectedId(comment.comment_id);
    onLocate?.(anchor || null);
  };
  const move = 

  direction => {
    const index = visibleRoots.findIndex(comment => comment.comment_id === selectedId);
    const nextIndex = index < 0 ? (direction > 0 ? 0 : visibleRoots.length - 1) : (index + direction + visibleRoots.length) % visibleRoots.length;
    const next = visibleRoots[nextIndex];
    if (!next) return;
    select(next);
    const card = [...(contentRef.current?.querySelectorAll('[data-comment-id]') || [])]
      .find(element => element.dataset.commentId === next.comment_id);
    card?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    card?.querySelector('.document-comment-select')?.focus({ preventScroll: true });
  };

  return <section className={`document-proofread-panel ${pane ? 'document-comments-pane' : 'documents-sidebar-accordion-section'} ${expanded ? 'is-expanded' : ''}`} aria-label="Comments" hidden={pane && !expanded}>
    {!pane && <button type="button" className="documents-sidebar-accordion-trigger" aria-expanded={expanded} onClick={onExpand}>
      <div className="documents-archive-section-title"><h2>Comments</h2><span>{openCount} open</span></div>
      <span aria-hidden="true">{expanded ? '−' : '+'}</span>
    </button>}
    {expanded && <>
      <header className="document-comments-header">
        <div><h2>Comments</h2><span>{run?.source_label || sourceVersion?.label || revisionId}</span></div>
        <button type="button" className="document-comment-new" onClick={() => setComposing(true)} disabled={!sourceHash}>
          <AppIcon icon="pi pi-plus" /> New comment
        </button>
      </header>
      <div className="document-comments-toolbar">
        <select aria-label="Show comments" value={filter} onChange={event => {
          setFilter(event.target.value); setSelectedId(null); onLocate?.(null);
        }}>
          <option value="active">Active ({openCount})</option>
          <option value="resolved">Resolved ({roots.length - openCount})</option>
          <option value="all">All comments ({roots.length})</option>
        </select>
        <div className="document-comments-navigation">
          <button type="button" aria-label="Previous comment" title="Previous comment" disabled={!visibleRoots.length} onClick={() => move(-1)}><AppIcon icon="pi pi-chevron-up" /></button>
          <button type="button" aria-label="Next comment" title="Next comment" disabled={!visibleRoots.length} onClick={() => move(1)}><AppIcon icon="pi pi-chevron-down" /></button>
        </div>
        {sourceVersion?.is_docx && <details className="document-comments-options">
          <summary aria-label="Comment options" title="Comment options"><AppIcon icon="pi pi-ellipsis-h" /></summary>
          <div className="document-comments-options-menu">
            <button type="button" disabled={busy || commentsLoading || Boolean(commentsError)} onClick={word}>Open commented copy in Word</button>
            <button type="button" disabled={busy || commentsLoading || Boolean(commentsError)} onClick={download}>Download commented copy</button>
          </div>
        </details>}
      </div>
      <div className="document-proofread-panel__content" ref={contentRef}>
        {error && <p role="alert">{error}</p>}
        {commentsError && <p role="alert">{commentsError}</p>}
        {commentsLoading && <p role="status">Loading comments…</p>}
        {active && <div className="document-comments-progress" role="status"><AppIcon icon="pi pi-spin pi-spinner" /><p>{run.progress || 'Proofreading…'}</p>
          <button type="button" disabled={busy} onClick={() => act(async current => {
            const response = await api.post(`${base}/${encodeURIComponent(run.run_id)}/cancel`);
            if (current()) setRun(response.data.run);
          })}>Cancel proofreading</button></div>}
        {run?.status === 'partial' && <p role="status"><strong>Proofreading incomplete</strong></p>}
        {run?.status === 'failed' && <p role="alert">{run.error || 'Proofreading failed.'}</p>}
        {run?.status === 'cancelled' && <p role="status">Proofreading cancelled.</p>}
        {(run?.warnings || []).map((warning, index) => <p className="document-proofread-muted" key={index}>{warning}</p>)}
        {ready && !run.comments?.some(c => !c.parent_comment_id) && <p role="status">{run.status === 'completed' ? 'No issues found in reviewed text.' : 'No verified findings in the completed portion.'}</p>}
        {composing && <form className="document-comment-composer" onSubmit={

        event => {
          event.preventDefault();
          const form = event.currentTarget;
          const body = new FormData(form).get('body');
          if (!String(body || '').trim()) return;
          act(async current => {
            await api.post(`/simul/archive/access/document/${encodeURIComponent(documentId)}/comments`, {
              body, source_revision_id: revisionId, source_content_sha256: sourceHash,
            });
            if (current()) { setComposing(false); setFilter('active'); }
            await refresh(current);
          });
        }}><strong>New comment</strong><textarea name="body" aria-label="New version comment" placeholder="Comment on this version…" maxLength={10000} required autoFocus />
          <footer><button type="button" disabled={busy} onClick={() => setComposing(false)}>Cancel</button><button className="is-primary" type="submit" disabled={busy || !sourceHash}>Add comment</button></footer></form>}
        {visibleRoots.map(comment => <article data-comment-id={comment.comment_id} aria-label={`Comment by ${comment.author_name || comment.author_id || 'Reviewer'}`}
          className={`document-proofread-comment ${comment.resolved_at ? 'is-resolved' : ''} ${selectedId === comment.comment_id ? 'is-selected' : ''}`} key={comment.comment_id}
          onClick={event => { if (!event.target.closest('button, textarea, input, select, summary, a')) select(comment); }}>
          <header><CommentAuthor comment={comment} />
            {comment.resolved_at ? <button type="button" className="document-comment-resolve" title="Reopen thread" disabled={busy} onClick={() => dispose(comment, 'open')}>Reopen</button>
              : <button type="button" className="document-comment-resolve" aria-label="Resolve" title="Resolve thread" disabled={busy} onClick={() => dispose(comment, 'resolved')}><AppIcon icon="pi pi-check" /></button>}
          </header>
          {comment.resolved_at && <span className="document-comment-state"><AppIcon icon="pi pi-check-circle" />{comment.disposition === 'dismissed' ? 'Dismissed' : 'Resolved'}</span>}
          {(comment.anchors || []).filter(anchor => anchor.quote).map((anchor, index) => <button type="button" className="document-proofread-quote document-comment-select" key={index}
            aria-pressed={selectedId === comment.comment_id} onClick={() => select(comment, anchor)} title="Show this passage in the document">“{anchor.quote}”<AppIcon icon="pi pi-external-link" /></button>)}
          {!comment.anchors?.some(anchor => anchor.quote) && <button type="button" className="document-comment-select document-comment-general" aria-pressed={selectedId === comment.comment_id} onClick={() => select(comment)}>Document comment</button>}
          <p>{comment.body}</p>
          {comment.suggestion && <p className="document-comment-suggestion"><AppIcon icon="pi pi-pencil" /><span>{comment.suggestion}</span></p>}
          {threadReplies(comments, comment.comment_id).map(item => <div className="document-proofread-reply" key={item.comment_id}><CommentAuthor comment={item} /><p>{item.body}</p></div>)}
          {replyTo === comment.comment_id ? <form className="document-comment-reply-composer" onSubmit={event => {
            event.preventDefault();
            if (!reply.trim()) return;
            act(async current => {
              await api.post(`/simul/archive/access/document/${encodeURIComponent(documentId)}/comments`, { body: reply, parent_comment_id: replyTo });
              if (current()) { setReplyTo(null); setReply(''); }
              await refresh(current);
            });
          }}><textarea aria-label="Reply to comment" placeholder="Reply…" value={reply} onChange={event => setReply(event.target.value)} maxLength={10000} autoFocus />
            <footer><button type="button" disabled={busy} onClick={() => setReplyTo(null)}>Cancel reply</button><button className="is-primary" disabled={busy || !reply.trim()} type="submit">Post reply</button></footer></form>
            : <footer><button type="button" className="document-comment-reply-button" disabled={busy} onClick={() => { select(comment); setReplyTo(comment.comment_id); setReply(''); }}>Reply</button>
              {comment.author_type === 'ai' && !comment.resolved_at && <button type="button" disabled={busy} onClick={() => dispose(comment, 'dismissed')}>Dismiss</button>}</footer>}
        </article>)}
        {!commentsLoading && !commentsError && visibleRoots.length === 0 && !composing && <div className="document-comments-empty"><AppIcon icon="pi pi-comments" />
          <p>{roots.length === 0 ? 'No comments on this version.' : filter === 'resolved' ? 'No resolved comments.' : 'All caught up.'}</p>
          {roots.length > 0 && <button type="button" onClick={() => setFilter('all')}>Show all comments</button>}
        </div>}
      </div>
      <footer className="document-comments-footer">
        {!active && <button type="button" disabled={busy || !sourceHash || !sourceVersion?.is_docx} onClick={start}><AppIcon icon="pi pi-sparkles" />{run ? 'Proofread again' : 'Proofread'}</button>}
        {history.length > 0 && <details className="document-proofread-history"><summary>Review history</summary><select aria-label="Proofreading history" value={run?.run_id || ''} disabled={busy} onChange={event => {
          const id = event.target.value;
          if (id) act(async current => { const response = await api.get(`${base}/${encodeURIComponent(id)}`); if (current()) { setRun(response.data.run); setSelectedId(null); onLocate?.(null); } });
        }}><option value="">Choose a review</option>
          {run && !history.some(item => item.run_id === run.run_id) && <option value={run.run_id}>Current review</option>}
          {history.map(item => <option key={item.run_id} value={item.run_id}>{new Date(item.created_at * 1000).toLocaleString()} · {item.status}</option>)}
        </select></details>}
      </footer>
    </>}
  </section>;
}
